/**
 * Structural Collapse — detect and collapse small wireframe fragments after cratering.
 *
 * After a crater destroys sand (wireframe survives), scan wireframe near the crater
 * for connected components via BFS. Any component that is fully contained within the
 * scan region AND has ≤ FRAGMENT_THRESHOLD cells is a broken fragment — collapse it
 * (set cells to EMPTY). Large islands and floating platforms remain self-anchored.
 */

import { TERRAIN, CELL, STRUCTURAL } from '../constants';
import { gridToWorldX, gridToWorldY, type GridBounds, type CollapseResult } from './grid';

// GRID_WIDTH accessed via TERRAIN.GRID_WIDTH (dynamic for wide stages)

/**
 * Scan wireframe near crater bounds, BFS connected components, collapse small fragments.
 * Returns CollapseResult if any cells were collapsed, null otherwise.
 */
export function detectAndCollapseFragments(
  grid: Uint8Array,
  craterBounds: GridBounds,
): CollapseResult | null {
  // 1. Expand crater bounds by SCAN_EXPAND, clamped to grid
  const expand = STRUCTURAL.SCAN_EXPAND;
  const scanMinGx = Math.max(0, craterBounds.minGx - expand);
  const scanMaxGx = Math.min(TERRAIN.GRID_WIDTH - 1, craterBounds.maxGx + expand);
  const scanMinGy = Math.max(0, craterBounds.minGy - expand);
  const scanMaxGy = Math.min(TERRAIN.GRID_HEIGHT - 1, craterBounds.maxGy + expand);

  const scanW = scanMaxGx - scanMinGx + 1;
  const scanH = scanMaxGy - scanMinGy + 1;
  if (scanW <= 0 || scanH <= 0) return null;

  // Visited bitmap (local coords within scan region)
  const visited = new Uint8Array(scanW * scanH);

  // Collect fragments to collapse
  const fragments: { gx: number; gy: number }[][] = [];

  // 4-connected neighbor offsets
  const dx = [1, -1, 0, 0];
  const dy = [0, 0, 1, -1];

  // BFS queue (reuse across components)
  const queue: number[] = [];

  // 2. Scan all wireframe cells in the region
  for (let gy = scanMinGy; gy <= scanMaxGy; gy++) {
    for (let gx = scanMinGx; gx <= scanMaxGx; gx++) {
      const localIdx = (gy - scanMinGy) * scanW + (gx - scanMinGx);
      if (visited[localIdx]) continue;
      if (grid[gy * TERRAIN.GRID_WIDTH + gx] !== CELL.WIREFRAME) continue;

      // 3. BFS flood-fill this component
      visited[localIdx] = 1;
      const component: { gx: number; gy: number }[] = [];
      let hasExternalConnection = false;

      queue.length = 0;
      queue.push(gx, gy);
      let qHead = 0;

      while (qHead < queue.length) {
        const cx = queue[qHead++];
        const cy = queue[qHead++];
        component.push({ gx: cx, gy: cy });

        for (let d = 0; d < 4; d++) {
          const nx = cx + dx[d];
          const ny = cy + dy[d];

          // Check if neighbor is outside scan region
          if (nx < scanMinGx || nx > scanMaxGx || ny < scanMinGy || ny > scanMaxGy) {
            // Neighbor is outside scan bounds — check if it's wireframe
            if (nx >= 0 && nx < TERRAIN.GRID_WIDTH && ny >= 0 && ny < TERRAIN.GRID_HEIGHT) {
              if (grid[ny * TERRAIN.GRID_WIDTH + nx] === CELL.WIREFRAME) {
                hasExternalConnection = true;
              }
            }
            continue;
          }

          const nLocalIdx = (ny - scanMinGy) * scanW + (nx - scanMinGx);
          if (visited[nLocalIdx]) continue;
          if (grid[ny * TERRAIN.GRID_WIDTH + nx] !== CELL.WIREFRAME) continue;

          visited[nLocalIdx] = 1;
          queue.push(nx, ny);
        }
      }

      // Component is a fragment if: no external connections AND small enough
      if (!hasExternalConnection && component.length <= STRUCTURAL.FRAGMENT_THRESHOLD) {
        fragments.push(component);
      }
    }
  }

  if (fragments.length === 0) return null;

  // 4. Collapse fragments — set cells to EMPTY, compute centroids + bounds
  let totalCells = 0;
  const centroids: { x: number; y: number }[] = [];
  let collapseMinGx: number = TERRAIN.GRID_WIDTH;
  let collapseMaxGx: number = 0;
  let collapseMinGy: number = TERRAIN.GRID_HEIGHT;
  let collapseMaxGy: number = 0;

  for (const frag of fragments) {
    let sumGx = 0;
    let sumGy = 0;

    for (const cell of frag) {
      grid[cell.gy * TERRAIN.GRID_WIDTH + cell.gx] = CELL.EMPTY;
      sumGx += cell.gx;
      sumGy += cell.gy;

      if (cell.gx < collapseMinGx) collapseMinGx = cell.gx;
      if (cell.gx > collapseMaxGx) collapseMaxGx = cell.gx;
      if (cell.gy < collapseMinGy) collapseMinGy = cell.gy;
      if (cell.gy > collapseMaxGy) collapseMaxGy = cell.gy;
    }

    totalCells += frag.length;
    centroids.push({
      x: gridToWorldX(Math.round(sumGx / frag.length)),
      y: gridToWorldY(Math.round(sumGy / frag.length)),
    });
  }

  return {
    cells: totalCells,
    bounds: {
      minGx: collapseMinGx,
      maxGx: collapseMaxGx,
      minGy: collapseMinGy,
      maxGy: collapseMaxGy,
    },
    centroids,
  };
}
